import { useEffect, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { DECIDED_BATCH_SIZE, DECIDED_LIST_LABELS } from './approvalDecidedList.definitions';
import { DayOffDecidedRow } from './DayOffDecidedRow';
import { VacationDecidedRow } from './VacationDecidedRow';
import type { ApprovalDecidedListProps } from './approvalDecidedList.types';

export function ApprovalDecidedList({
    items,
    isLoading,
}: ApprovalDecidedListProps) {
    const [visibleCount, setVisibleCount] = useState(DECIDED_BATCH_SIZE);
    useEffect(() => {
        setVisibleCount((current) =>
            Math.max(DECIDED_BATCH_SIZE, Math.min(current, items.length))
        );
    }, [items.length]);
    if (isLoading || items.length === 0) return null;
    const visible = items.slice(0, visibleCount);
    const remaining = items.length - visible.length;
    const isExpanded = visibleCount > DECIDED_BATCH_SIZE;
    return (
        <section aria-labelledby="decided-list-title">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2
                    id="decided-list-title"
                    className="text-sm font-semibold text-raro-gray-900 font-montserrat"
                >
                    {DECIDED_LIST_LABELS.title}
                </h2>
                <span className="text-xs text-raro-gray-500 font-montserrat">
                    {DECIDED_LIST_LABELS.count(items.length)}
                </span>
            </div>
            <p className="mt-1 text-xs text-raro-gray-500 font-montserrat">
                {DECIDED_LIST_LABELS.subtitle}
            </p>
            <ul className="mt-3 divide-y divide-gray-100 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
                {visible.map((item) => (
                    item.kind === 'vacation' ? (
                        <VacationDecidedRow key={`vacation-${item.id}`} approval={item} />
                    ) : (
                        <DayOffDecidedRow key={`day_off-${item.id}`} approval={item} />
                    )
                ))}
            </ul>
            {(remaining > 0 || isExpanded) && (
                <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
                    {remaining > 0 && (
                        <button
                            type="button"
                            onClick={() =>
                                setVisibleCount((current) => current + DECIDED_BATCH_SIZE)
                            }
                            className="inline-flex items-center gap-1.5 rounded-xl border border-primary-200 bg-white px-4 py-2 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 font-montserrat"
                        >
                            <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
                            {DECIDED_LIST_LABELS.showMore(remaining)}
                        </button>
                    )}
                    {isExpanded && (
                        <button
                            type="button"
                            onClick={() => setVisibleCount(DECIDED_BATCH_SIZE)}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-transparent px-4 py-2 text-sm font-semibold text-raro-gray-500 transition-colors hover:text-raro-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 font-montserrat"
                        >
                            <ChevronUpIcon className="h-4 w-4" aria-hidden="true" />
                            {DECIDED_LIST_LABELS.showLess}
                        </button>
                    )}
                    <span
                        className="text-xs text-raro-gray-400 font-montserrat"
                        aria-live="polite"
                    >
                        {DECIDED_LIST_LABELS.shown(visible.length, items.length)}
                    </span>
                </div>
            )}
        </section>
    );
}
export default ApprovalDecidedList;
